import { useSnackbar } from 'notistack';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'motion/react';
import { alpha } from '@mui/material/styles';
import { Stack, Divider, Typography, Box } from '@mui/material';
import { fadeContainerVariants, fadeVariants } from '../../../utils/animations';
import useAuth from '../../../hooks/useAuth';
import useActingRole from '../../../hooks/useActingRole';
import useIsMountedRef from '../../../hooks/useIsMountedRef';
import useLocales from '../../../hooks/useLocales';
import useSettings from '../../../hooks/useSettings';
import MyAvatar from '../../../components/MyAvatar';
import MenuPopover from '../../../components/MenuPopover';
import MenuPopoverItem from '../../../components/MenuPopoverItem';
import Iconify from '../../../components/Iconify';
import AntSwitch from '../../../components/AntSwitch';
import { IconButtonAnimate } from '../../../components/animate';
import SettingsDrawer from '../../../components/settings/drawer';
import ReachlinkPlusSubscriptionCard from '../../../components/subscription/ReachlinkPlusSubscriptionCard';
import { fPhone } from '../../../utils/formatNumber';
import CareCircleNav from 'src/components/carecircle/CareCircleNav';
import { PATH_MAIN } from '../../../routes/paths';

const AccountPopover = () => {
    const { user, logout, isAdmin, isClient } = useAuth();
    const isReachlinkPlusOffered = user?.reachlinkPlusEnabled !== false;
    const { openDialog, hasMultipleRoles } = useActingRole();
    const { translate } = useLocales();
    const { themeMode, onChangeMode } = useSettings();
    const navigate = useNavigate();

    const isMountedRef = useIsMountedRef();

    const { enqueueSnackbar } = useSnackbar();

    const [open, setOpen] = useState<HTMLElement | null>(null);

    const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
        setOpen(event.currentTarget);
    };

    const handleClose = () => {
        setOpen(null);
    };

    const handleNavigateToAccount = () => {
        handleClose();
        navigate(PATH_MAIN.account);
    };

    const handleNavigateToBillingAndSubscription = () => {
        handleClose();
        navigate(PATH_MAIN.billingAndSubscription);
    };

    const handleLogout = () => {
        try {
            logout();

            if (isMountedRef.current) {
                handleClose();
            }
        } catch (error) {
            console.error(error);
            enqueueSnackbar(translate('layouts.accountPopover.logoutError'), { variant: 'error' });
        }
    };

    return (
        <>
            <IconButtonAnimate
                onClick={handleOpen}
                aria-label={translate('layouts.accountPopover.menuAria')}
                aria-haspopup="menu"
                aria-expanded={Boolean(open)}
                data-testid="account-menu-trigger"
                sx={{
                    p: 0,
                    ...(open && {
                        '&:before': {
                            zIndex: 1,
                            content: "''",
                            width: '100%',
                            height: '100%',
                            borderRadius: '50%',
                            position: 'absolute',
                            bgcolor: (theme) => alpha(theme.palette.grey[900], 0.8),
                        },
                    }),
                }}
            >
                <MyAvatar />
            </IconButtonAnimate>

            <MenuPopover
                open={Boolean(open)}
                anchorEl={open}
                onClose={handleClose}
                disabledArrow
                sx={{
                    minWidth: 320,
                    p: 0,
                    mt: 1.5,
                    ml: 0.75,
                    '& .MuiMenuItem-root': {
                        typography: 'body2',
                        borderRadius: 0.75,
                    },
                }}
            >
                <Stack
                    component={motion.div}
                    variants={fadeContainerVariants}
                    custom={{ staggerChildren: 0.05 }}
                    initial="initial"
                    animate="animate"
                    sx={{ gap: 2, p: 2 }}
                >
                    <Box component={motion.div} variants={fadeVariants}>
                        <Stack sx={{ flexDirection: 'row', alignItems: 'center', gap: 1.5 }}>
                            <MyAvatar sx={{ width: 40, height: 40 }} />
                            <Stack sx={{ gap: 0.25 }}>
                                <Typography variant="subtitle2" noWrap>
                                    {user?.name}
                                </Typography>
                                <Typography
                                    variant="caption"
                                    sx={{ color: 'text.secondary' }}
                                    noWrap
                                >
                                    {fPhone(user?.phoneNumber)}
                                </Typography>
                                <Typography
                                    variant="caption"
                                    sx={{ color: 'text.secondary' }}
                                    noWrap
                                >
                                    {user?.email}
                                </Typography>
                            </Stack>
                        </Stack>
                    </Box>

                    {isClient && isReachlinkPlusOffered && (
                        <Box component={motion.div} variants={fadeVariants}>
                            <ReachlinkPlusSubscriptionCard />
                        </Box>
                    )}

                    <Box component={motion.div} variants={fadeVariants}>
                        <MenuPopoverItem onClick={handleNavigateToAccount}>
                            <Iconify
                                icon="material-symbols:account-circle-outline"
                                width={20}
                                height={20}
                            />
                            <Typography variant="body2">
                                {translate('layouts.accountPopover.accountSettings')}
                            </Typography>
                        </MenuPopoverItem>
                    </Box>

                    {isClient && (
                        <>
                            <Box component={motion.div} variants={fadeVariants}>
                                <MenuPopoverItem onClick={handleNavigateToBillingAndSubscription}>
                                    <Iconify
                                        icon="material-symbols:credit-card-outline"
                                        width={20}
                                        height={20}
                                    />
                                    <Typography variant="body2">
                                        {translate(
                                            'layouts.accountPopover.billingAndSubscriptions'
                                        )}
                                    </Typography>
                                </MenuPopoverItem>
                            </Box>
                            {isReachlinkPlusOffered && (
                                <Box component={motion.div} variants={fadeVariants}>
                                    <CareCircleNav />
                                </Box>
                            )}
                        </>
                    )}

                    <Divider />

                    <Box component={motion.div} variants={fadeVariants}>
                        {isAdmin ? (
                            <SettingsDrawer menuItem />
                        ) : (
                            <MenuPopoverItem>
                                <Iconify
                                    icon="material-symbols:dark-mode-outline"
                                    width={20}
                                    height={20}
                                />
                                <Typography variant="body2" sx={{ flexGrow: 1 }}>
                                    {translate('components.settings.drawer.darkMode')}
                                </Typography>
                                <AntSwitch
                                    width={33}
                                    height={20}
                                    checked={themeMode === 'dark'}
                                    onChange={(event) =>
                                        onChangeMode({
                                            target: {
                                                value: event.target.checked ? 'dark' : 'light',
                                            },
                                        } as any)
                                    }
                                />
                            </MenuPopoverItem>
                        )}
                    </Box>

                    {hasMultipleRoles && (
                        <>
                            <Box component={motion.div} variants={fadeVariants}>
                                <MenuPopoverItem onClick={openDialog}>
                                    {translate('layouts.accountPopover.switchRole')}
                                </MenuPopoverItem>
                            </Box>
                            <Divider />
                        </>
                    )}

                    <Box component={motion.div} variants={fadeVariants}>
                        <MenuPopoverItem onClick={handleLogout}>
                            <Iconify icon="material-symbols:logout" width={20} height={20} />
                            <Typography variant="body2" sx={{ flexGrow: 1 }}>
                                {translate('layouts.accountPopover.logout')}
                            </Typography>
                        </MenuPopoverItem>
                    </Box>
                </Stack>
            </MenuPopover>
        </>
    );
};

export default AccountPopover;
